import { useEffect, useMemo, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraSimpleGrid,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  Text,
  TextAreaInputV2,
  TextInputV2,
  UniversalSelect,
  useToastMessage,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';

import Header from '~/components/common/Header';
import { PAGE_LAYOUT, PAGE_TOP_PADDING } from '~/lib/constants';
import { getSelectedUniversalSelectOption } from '~/lib/util/inquiry.util';
import { Path } from '~/routes/constants';
import { useAddNoteTemplateRevisionMutation } from '~/store/services/notes/noteTemplateRevisions';
import { useGetNoteTemplatesQuery } from '~/store/services/notes/noteTemplates';

const TOP_LEVEL_ID = 'edit-note-template';
type EditNoteTemplateFieldErrors = {
  selectedTemplate?: boolean;
  templateTitle?: boolean;
  templateJson?: boolean;
  surveyJsId?: boolean;
};

const ModifyNoteTemplateRevisions = () => {
  const history = useHistory();
  const { createToast } = useToastMessage();
  const hasShownToast = useRef(false);

  const [selectedTemplate, setSelectedTemplate] = useState<NoteTemplate>();
  const [templateJson, setTemplateJson] = useState<string>();
  const [surveyJsId, setSurveyJsId] = useState<string>();

  const [formFieldErrors, setFormFieldErrors] =
    useState<EditNoteTemplateFieldErrors>({
      selectedTemplate: false,
      surveyJsId: false,
      templateJson: false,
    });
  const { data: noteTemplatesList, isLoading: isGetNoteTemplatesLoading } =
    useGetNoteTemplatesQuery();

  const [
    addNoteTemplateRevision,
    {
      isSuccess: isAddNoteTemplateRevisionSuccess,
      isLoading: isAddNoteTemplateRevisionLoading,
      isError: isAddNoteTemplateRevisionError,
    },
  ] = useAddNoteTemplateRevisionMutation();

  const templateOptions = useMemo<UniversalSelectOption[]>(() => {
    return (noteTemplatesList ?? []).map(
      ({ templateTitle, templateId }: NoteTemplate) => ({
        label: templateTitle,
        value: templateId,
      }),
    );
  }, [noteTemplatesList]);

  const getTemplateFromSelectedOption = (
    selected: UniversalSelectOption,
  ): NoteTemplate | undefined => {
    return noteTemplatesList?.find(
      (template: NoteTemplate) => template?.templateId === selected.value,
    );
  };
  const handleTemplateSelectChange = (selected: UniversalSelectOption) => {
    const selectedTemplate: NoteTemplate =
      getTemplateFromSelectedOption(selected);
    setSelectedTemplate(selectedTemplate);
  };

  const resetForm = () => {
    setSelectedTemplate(undefined);
    setTemplateJson('');
    setSurveyJsId('');
  };

  const isTemplateJsonValid = (): boolean => {
    if (!templateJson) {
      return true;
    }
    if (!templateJson.trim()) {
      return false;
    }
    try {
      JSON.parse(templateJson.trim());
      return true;
    } catch (e) {
      return false;
    }
  };

  const validateForm = () => {
    setFormFieldErrors({
      selectedTemplate: !selectedTemplate,
      surveyJsId: !!templateJson && !surveyJsId,
      templateJson: !isTemplateJsonValid(),
    });
  };

  const resetHasShownToastRef = () => {
    hasShownToast.current = true;
    setTimeout(() => {
      hasShownToast.current = false;
    }, 2000);
  };
  const handleSave = () => {
    validateForm();
    const isFormValid = !Object.values(formFieldErrors).some((error) => error);
    if (isFormValid) {
      const templateJsonValue = JSON.parse(templateJson.trim());
      if (templateJsonValue) {
        addNoteTemplateRevision({
          surveyJsId,
          templateId: selectedTemplate?.templateId,
          templateJson: templateJsonValue,
        })
          .unwrap()
          .then(() => {
            createToast({
              icon: FAIconName.checkRegular,
              id: 'add-note-template-revision-success_toast',
              text: 'Added Note Template Revision successfully.',
              variant: 'success',
            });
            resetHasShownToastRef();
          })
          .catch(() => {
            createToast({
              icon: FAIconName.exclamationTriangleRegular,
              id: 'add-note-template-revision-error_toast',
              text: "We're sorry, we were unable to add note template revision. Please try again",
              variant: 'critical',
            });
            resetHasShownToastRef();
          });
      }
    }
  };

  const isNoteTemplateRevisionFieldsChanged =
    templateJson && surveyJsId && isTemplateJsonValid();

  const isSaveDisabled = (): boolean => {
    return (
      !selectedTemplate ||
      !isNoteTemplateRevisionFieldsChanged ||
      isAddNoteTemplateRevisionError
    );
  };
  const isSaveLoading = (): boolean => {
    return isAddNoteTemplateRevisionLoading;
  };

  useEffect(() => {
    if (
      isNoteTemplateRevisionFieldsChanged &&
      isAddNoteTemplateRevisionSuccess
    ) {
      resetForm();
    }
  }, [isAddNoteTemplateRevisionSuccess]);

  const TitleAndValues = ({
    id,
    title,
    values,
  }: {
    id: string;
    title: string;
    values: string[];
  }) => {
    return (
      <ChakraFlex flexDirection="column" gap="6px">
        <Text color="neutral.secondary" id={`${id}_title`} variant="caption">
          {title}
        </Text>
        {values &&
          values.map((v) => (
            <Text id={`${id}_value_${v}`} key={`${id}_value_${v}`}>
              {v}
            </Text>
          ))}
      </ChakraFlex>
    );
  };

  return (
    <ChakraBox
      paddingRight={PAGE_LAYOUT.rightSpacing}
      paddingTop={PAGE_LAYOUT.topSpacing}
    >
      <Header title="Note Template Revisions" />
      <ChakraBox gap="40px" marginTop={PAGE_TOP_PADDING}>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__reset-button`}
          onClick={() => {
            resetForm();
            history.push(Path.ADMIN_MANAGE_CONTENT);
          }}
          variant="tertiary"
        >
          Back to manage content
        </FireflyButton>
      </ChakraBox>
      <ChakraBox
        borderRadius="12px"
        borderWidth="1px"
        marginTop={PAGE_TOP_PADDING}
        padding="18px"
      >
        <InputGroupV2
          id={`${TOP_LEVEL_ID}_template__group`}
          isRequired
          label="Choose Note Template"
          shouldShowAsteriskWhenRequired
        >
          <UniversalSelect
            id={`${TOP_LEVEL_ID}_template__select`}
            isClearable={false}
            isInvalid={formFieldErrors.selectedTemplate}
            isLoading={isGetNoteTemplatesLoading}
            isSingleSelect
            onChange={handleTemplateSelectChange}
            options={templateOptions}
            value={getSelectedUniversalSelectOption(
              selectedTemplate?.templateId,
              templateOptions,
            )}
          />
        </InputGroupV2>
        {selectedTemplate && (
          <>
            <Text marginBottom="20px" marginTop="20px" variant="h5">
              Selected Note Template
            </Text>
            <ChakraSimpleGrid columns={3} spacing="24px" width="100%">
              <TitleAndValues
                id={`${TOP_LEVEL_ID}_templateTitle`}
                title="Template Title"
                values={[selectedTemplate?.templateTitle]}
              />
              <TitleAndValues
                id={`${TOP_LEVEL_ID}_providerTypes`}
                title="Provider Types"
                values={selectedTemplate?.providerTypes}
              />
              <TitleAndValues
                id={`${TOP_LEVEL_ID}_appointmentTypeLabels`}
                title="Appointment Type Labels"
                values={selectedTemplate?.appointmentTypeLabels}
              />
            </ChakraSimpleGrid>

            <Text marginBottom="20px" marginTop="20px" variant="h5">
              Add Note Template Revision
            </Text>
            <InputGroupV2
              id={`${TOP_LEVEL_ID}_surveyJs__group`}
              isRequired
              label="SurveyJS ID"
              shouldHideOptionalLabel
              shouldShowAsteriskWhenRequired
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}_surveyJs__input`}
                isInvalid={formFieldErrors.surveyJsId}
                onChange={setSurveyJsId}
                value={surveyJsId}
              />
            </InputGroupV2>
            <InputGroupV2
              id={`${TOP_LEVEL_ID}_templateJson__group`}
              isRequired
              label="Template JSON"
              shouldShowAsteriskWhenRequired
            >
              <TextAreaInputV2
                id={`${TOP_LEVEL_ID}_templateJson__input`}
                isInvalid={!isTemplateJsonValid()}
                onChange={setTemplateJson}
                value={templateJson}
              />
            </InputGroupV2>
            <ChakraFlex
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <FireflyButton
                id={`${TOP_LEVEL_ID}_reset__button`}
                onClick={resetForm}
                variant="secondary"
              >
                Reset
              </FireflyButton>
              <FireflyButton
                id={`${TOP_LEVEL_ID}_save__button`}
                isDisabled={isSaveDisabled()}
                isLoading={isSaveLoading()}
                leftIcon={FAIconName.checkRegular}
                onClick={handleSave}
              >
                Save
              </FireflyButton>
            </ChakraFlex>
          </>
        )}
      </ChakraBox>
    </ChakraBox>
  );
};

export default ModifyNoteTemplateRevisions;
